import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, Paper, IconButton, Typography, Avatar } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import MessagesList from "../MessagesList";
import MessageInput from "../MessageInput";
import ContactDrawer from "../ContactDrawer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { isNil } from "lodash";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  mainWrapperShift: {
    marginRight: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    backgroundColor: theme.palette.tabHeaderBackground,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: "56px",
    cursor: "pointer",
  },
  headerInfo: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "12px",
    flex: 1,
    overflow: "hidden",
  },
  contactName: {
    fontWeight: 600,
    fontSize: "15px",
    color: theme.palette.textPrimary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactNumber: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
  },
  avatar: {
    width: 40,
    height: 40,
    cursor: "pointer",
  },
}));

const DirectTicket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);

  // Fetch ticket and auto-reopen if closed/pending
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          if (!isNil(ticketId) && ticketId !== "undefined") {
            const { data } = await api.get("/tickets/u/" + ticketId);
            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user]);

  // Socket listeners
  useEffect(() => {
    if (!ticket?.id || !socket) return;

    const onConnect = () => {
      socket.emit("joinChatBox", `${ticket.id}`);
    };

    const onTicketUpdate = (data) => {
      if (data.action === "update" && data.ticket?.id === ticket?.id) {
        setTicket(data.ticket);
      }
    };

    const onContactUpdate = (data) => {
      if (data.action === "update" && data.contact?.id === contact?.id) {
        setContact((prev) => ({ ...prev, ...data.contact }));
      }
    };

    socket.on("connect", onConnect);
    socket.on(`company-${companyId}-ticket`, onTicketUpdate);
    socket.on(`company-${companyId}-contact`, onContactUpdate);
    onConnect();

    return () => {
      socket.emit("joinChatBoxLeave", `${ticket.id}`);
      socket.off("connect", onConnect);
      socket.off(`company-${companyId}-ticket`, onTicketUpdate);
      socket.off(`company-${companyId}-contact`, onContactUpdate);
    };
  }, [ticket?.id, contact?.id, companyId, socket]);

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={`${classes.mainWrapper} ${drawerOpen ? classes.mainWrapperShift : ""}`}
      >
        {/* Simple header - just contact info, no action buttons */}
        <div className={classes.header} onClick={handleDrawerOpen}>
          <Avatar
            className={classes.avatar}
            src={contact.profilePicUrl || contact.urlPicture}
            alt={contact.name}
          >
            {contact.name?.charAt(0).toUpperCase()}
          </Avatar>
          <div className={classes.headerInfo}>
            <Typography className={classes.contactName}>
              {contact.name || "Carregando..."}
            </Typography>
            <Typography className={classes.contactNumber}>
              {contact.number || ""}
            </Typography>
          </div>
        </div>

        {/* Messages + Input - always "open" status, no restrictions */}
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              <MessagesList
                isGroup={ticket.isGroup}
                onDrop={setDragDropFiles}
                whatsappId={ticket.whatsappId}
                queueId={ticket.queueId}
                channel={ticket.channel}
                ticketStatus="open"
                fullHistory
                contactId={contact.id}
              />
              <MessageInput
                ticketId={ticket.id}
                ticketStatus="open"
                ticketChannel={ticket.channel}
                droppedFiles={dragDropFiles}
                contactId={contact.id}
                whatsappId={ticket.whatsappId}
              />
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />
    </div>
  );
};

export default DirectTicket;
